import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { readUtm, trackLeadConversion } from "@/lib/adsTracking";
import { FORM_OPTIONS } from "./hcaData";
import { HcaSelect, HcaPills } from "./HcaChoice";
import { HcaLeadSuccess } from "./HcaLeadSuccess";

const EMPTY = { name: "", email: "", phone: "", status_in_canada: "", english_test_status: "", best_time_to_call: "", police_clearance: "", website: "" };
const REQUIRED = ["name", "email", "phone", "status_in_canada", "english_test_status", "best_time_to_call", "police_clearance"];

const TextField = ({ id, label, testId, ...props }) => (
    <div className="space-y-1.5">
        <Label htmlFor={id} className="text-xs font-semibold text-[#0E1E3A]">{label}</Label>
        <Input id={id} data-testid={testId} className="h-11 bg-white border-[#D8DEE6]" required {...props} />
    </div>
);

export const HcaLeadForm = () => {
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (REQUIRED.some((k) => !form[k].trim())) {
            toast.error("Please answer every question so an advisor can help you.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/leads", { ...form, ...readUtm(), source_url: window.location.href });
            trackLeadConversion();
            setDone(form);
            toast.success("Thanks! An admissions advisor will contact you shortly.");
        } catch (err) {
            toast.error(err?.response?.status === 422 ? "Please check your email and phone number." : "Something went wrong. Please call us instead.");
        } finally {
            setBusy(false);
        }
    };

    if (done) return <HcaLeadSuccess lead={done} />;

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="hca-lead-form" noValidate>
            <TextField id="hca-name" label="Full name" testId="hca-lead-form-input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" autoComplete="name" />
            <div className="grid sm:grid-cols-2 gap-4">
                <TextField id="hca-email" label="Email" type="email" testId="hca-lead-form-input-email" value={form.email} onChange={set("email")} placeholder="jane@example.com" autoComplete="email" />
                <TextField id="hca-phone" label="Phone" type="tel" testId="hca-lead-form-input-phone" value={form.phone} onChange={set("phone")} placeholder="(604) 555-0199" autoComplete="tel" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <HcaSelect id="hca-status" label="Status in Canada" value={form.status_in_canada} onChange={set("status_in_canada")} options={FORM_OPTIONS.status} testId="hca-lead-form-select-status" />
                <HcaSelect id="hca-english" label="English test status" value={form.english_test_status} onChange={set("english_test_status")} options={FORM_OPTIONS.english} testId="hca-lead-form-select-english" />
            </div>
            <HcaPills label="Do you have a police clearance certificate?" value={form.police_clearance} onChange={set("police_clearance")} options={FORM_OPTIONS.police} testId="hca-lead-form-police" />
            <HcaSelect id="hca-time" label="Best time to call" value={form.best_time_to_call} onChange={set("best_time_to_call")} options={FORM_OPTIONS.time} testId="hca-lead-form-select-time" />
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <Button type="submit" disabled={busy} data-testid="hca-lead-form-submit-btn" className="w-full h-12 rounded-full bg-[#D4A140] hover:bg-[#c08f33] text-[#0E1E3A] font-semibold text-base group">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Get my program info <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#5E6B7E]">
                <Lock className="h-3 w-3" /> Your details go only to our admissions team. No spam.
            </p>
        </form>
    );
};
